/**
 * Every word the Water Quality Scorecard puts on screen, in one place so the
 * wording can be revised without touching a component.
 *
 * Keep it accurate to what the tool actually computes. The numbers come from
 * viewer_endpoints.build_scorecard_period(), which is a transcription of the
 * same SQL the interactive map uses, so any claim made here should hold for
 * the map too.
 */

export const PAGE_TITLE = "Water Quality Scorecard";

export const PAGE_INTRO =
  "Each cell shows a single summary statistic for one monitoring site and one " +
  "parameter over the period named above the table. Shading places that value " +
  "on the Cherry Creek Basin Water Quality Authority's five-band benchmark " +
  "scale, and the arrow reports the direction of change over the trend period, " +
  "which may be longer than the period the value itself covers. Stream sites " +
  "are listed from upstream to downstream within each stream, with Cherry " +
  "Creek Reservoir as the last group. The three reservoir standard columns " +
  "work differently: they show July to September seasonal means and are " +
  "colored by attainment of the reservoir's regulatory values rather than by " +
  "benchmark band.";

export const DISCLAIMER =
  "Benchmark shading compares each value to CCBWQA benchmarks, which are " +
  "planning and screening targets, and a shaded cell does not by itself " +
  "indicate a regulatory exceedance. The reservoir standard columns compare " +
  "seasonal means to the values in Regulations 38 and 31 for reference only; " +
  "this page is not an assessment of attainment with Colorado water quality " +
  "standards, which the state carries out under its own listing methodology.";

/**
 * The four explanatory sections, modelled on the Keep It Clean Partnership
 * scorecard. Rendered as accordions, collapsed by default.
 */
export const ACCORDIONS = [
  {
    id: "how-to-read",
    title: "How to read the scorecard",
    paragraphs: [
      "Rows are monitoring sites, grouped by stream and ordered from upstream " +
        "to downstream, with the reservoir stations and a whole-reservoir " +
        "composite as the last group when the reservoir switch is on. Columns " +
        "are parameters. Each cell combines three pieces of information: the " +
        "value, the benchmark band it falls in (or, in the reservoir standard " +
        "columns, whether the standard is being met), and the trend.",
      "Use the controls above the table to change what the values mean. The " +
        "period sets the window the statistic is calculated over. The flow " +
        "condition limits the samples to those collected during baseflow or " +
        "during storms, or uses every sample. The statistic switches between " +
        "the median, which describes typical conditions, and the 85th " +
        "percentile, which describes the higher end of what was measured.",
      "A value shown lighter with n = x beneath it rests on fewer than six " +
        "samples. A median drawn from a handful of samples can move a long way " +
        "on one result, so treat it as indicative rather than settled. The " +
        "trend arrow beside it is unaffected: trends are tested over their own, " +
        "usually longer, window and are only drawn when that window holds five " +
        "or more samples.",
      "A cell reading n/a means the site is monitored for that parameter but " +
        "no samples fell inside the period and flow condition; the tooltip " +
        "gives the date it was last sampled. A dash means the site has never " +
        "been sampled for that parameter, or the column does not apply to it. " +
        "Neither means the value was zero or that the site was clean.",
      "Hover over any cell for the full detail: the value and its units, the " +
        "benchmark band, the number of samples, the dates actually sampled, " +
        "and the trend statistics behind the arrow.",
      "On the Live period, cells with data carry a small map icon. Selecting " +
        "it opens the interactive Water Quality Map for that site and parameter " +
        "in a new tab, keeping the flow condition and statistic you are " +
        "currently viewing, so the scorecard stays where you left it. The map " +
        "icon beside each site name centers the map on that site from any " +
        "period. Reservoir standard cells carry a small calendar icon that " +
        "opens a season-by-season table.",
    ],
  },
  {
    id: "benchmarks",
    title: "How benchmark shading is determined",
    paragraphs: [
      "Every parameter with a benchmark has five bands, and a value is shaded " +
        "by the band it falls into. The bands run from below detection limits, " +
        "through below benchmark and approaching benchmark, to above benchmark " +
        "and above secondary benchmark. Parameters with no benchmark defined are " +
        "shaded neutrally and labeled accordingly. On reservoir rows the " +
        "nutrient columns are deliberately left unshaded; see the reservoir " +
        "section below.",
      "For most parameters a higher value is the worse outcome. For a few — " +
        "dissolved oxygen is the clearest example — a low value is the problem, " +
        "so the scale is inverted and the 15th percentile is used in place of " +
        "the 85th. pH is handled differently again, because both a low and a " +
        "high result are undesirable; the band shown is whichever of the two " +
        "tails scores worse.",
      "Shading is always accompanied by the band name in the cell's tooltip and " +
        "in the text read by screen readers, so the color is never the only way " +
        "the information is conveyed.",
      "The same benchmarks and the same band boundaries drive the interactive " +
        "Water Quality Map, so a value here and the same value there will always " +
        "carry the same color.",
    ],
  },
  {
    id: "trends",
    title: "How trends are calculated",
    paragraphs: [
      "Trends use the Mann-Kendall test, a rank-based method that looks at the " +
        "direction of change between every pair of samples rather than fitting a " +
        "line. It makes no assumption about the shape of the data, which suits " +
        "water quality records with occasional extreme results. Non-detects are " +
        "treated as zero.",
      "A result is reported as increasing or decreasing when the test is " +
        "significant at the 95 percent level, and as probably increasing or " +
        "probably decreasing at the 90 percent level.",
      "When no significant direction is found, the scorecard distinguishes two " +
        "cases using the coefficient of variation, the standard deviation " +
        "divided by the mean. A record with a coefficient of variation at or " +
        "below one is reported as stable: consistent, with no direction. Above " +
        "one it is reported as no trend: too variable for the test to resolve a " +
        "direction either way.",
      "At least five samples are needed in the trend period. Below that the " +
        "scorecard reports that there is not enough data rather than showing an " +
        "arrow.",
      "The trend period is often longer than the period the value covers. The " +
        "current water year, for instance, shows this year's numbers alongside a " +
        "trend measured over the six water years ending in it, so that a single " +
        "year's results are read against the recent record. The exact dates for " +
        "both are in every cell's tooltip, along with the span the trend " +
        "samples actually cover, which on a rarely sampled parameter can be a " +
        "few years inside a much longer window.",
    ],
  },
  {
    id: "reservoir",
    title: "How the reservoir is assessed",
    paragraphs: [
      "Cherry Creek Reservoir is judged differently from the streams that feed " +
        "it. Colorado's Regulation 38 sets a chlorophyll-a standard for the " +
        "reservoir, and Regulation 31 provides interim total phosphorus and " +
        "total nitrogen values for warm reservoirs. All three are seasonal " +
        "means: the average of samples taken from July to September, with one " +
        "exceedance allowed in any five years.",
      "The three regulatory columns at the left of the reservoir rows report " +
        "those seasonal means. The number is the average of the seasonal means " +
        "inside the chosen period. The color is attainment: green when no more " +
        "than one season in five exceeded the standard, red when more did, and " +
        "neutral when no season had enough samples to judge. A one-year period " +
        "is simply met or not met. The tooltip lists which summers met and " +
        "which exceeded; the calendar icon in the cell opens a table with each " +
        "season's mean, sample count and verdict.",
      "The total phosphorus and total nitrogen values are interim table values " +
        "from Regulation 31 and are marked as such; a site-specific standard " +
        "for the reservoir has not been adopted. The chlorophyll-a value is the " +
        "reservoir's own standard.",
      "The remaining reservoir columns use the same water-year statistics as " +
        "the stream rows. Field parameters such as dissolved oxygen, pH and " +
        "temperature keep their benchmark shading, because those bands are not " +
        "specific to streams. The nutrient columns on the stream benchmark " +
        "scale are shown without shading on reservoir rows: those bands were " +
        "set for flowing water, and a lake value colored on them would read as " +
        "cleaner than its own standard says it is.",
      "Reservoir samples carry no flow condition, so the flow control has no " +
        "effect on reservoir rows; they always use every sample. The CCR " +
        "Composite row averages the three stations on each sampling date before " +
        "any statistic is calculated, which is how the Water Quality Trends " +
        "page computes the whole-reservoir figures. On the Live period the " +
        "seasons are those beginning inside the site-wide analysis window, so " +
        "a one-year window judges a single summer.",
    ],
  },
];

export const CONTROL_LABELS = {
  period: "Period",
  flow: "Flow condition",
  statistic: "Statistic",
  parameters: "Parameters",
  scorecard: "Scorecard",
  reservoir: "Include Cherry Creek Reservoir",
};

/** Under the period line when reservoir rows are on screen. */
export const RESERVOIR_PERIOD_NOTE =
  "The three reservoir standard columns are always shown while the reservoir " +
  "is included and are not part of the parameter list; they use the July to " +
  "September seasons that begin inside this period. The flow condition does " +
  "not apply to reservoir rows.";

export const FLOW_OPTIONS = [
  { value: "alldata", label: "All data" },
  { value: "baseflow", label: "Baseflow" },
  { value: "stormflow", label: "Stormflow" },
];

export const STATISTIC_OPTIONS = [
  { value: "median", label: "Median" },
  { value: "pctile", label: "85th percentile" },
];

/** Shown beside the Apply button. This action is not local to this page. */
export const LIVE_REBUILD_WARNING =
  "Applying these dates changes the analysis window for the whole site, " +
  "including the interactive map, for everyone. The recalculation takes a few " +
  "minutes, and the page will switch to Live to show the result.";

/**
 * The rebuild moves the statistics window but not the trend window, which is
 * held separately and starts in 2008. Without saying so, a reader would take
 * the arrow to describe the dates in the pickers.
 */
export const LIVE_TREND_NOTE =
  "Only the statistics window moves. Trends are always calculated over the " +
  "site-wide trend period, which begins in 2008 and runs to the present, so " +
  "on Live the arrow may cover a longer span than the value beside it. Each " +
  "cell's tooltip gives both.";

export const EMPTY_STATE =
  "No results for this combination of period and flow condition. Try All data, " +
  "or a longer period.";

export const SMALL_SAMPLE_NOTE = "Fewer than six samples";

/**
 * Why the per-cell map links vanish outside Live. The map's graph mode always
 * draws the site-wide analysis window, so a link from a snapshot could open a
 * map that disagrees with the cell it came from.
 */
export const MAP_LINK_LIVE_ONLY =
  "The map always shows the live analysis window, so links from a cell to the " +
  "water quality map are offered on the Live period only. The map icon beside " +
  "each site works from any period and simply centers the map there.";

/**
 * Band names, replacing the ones in data.list_benchmark_colors.
 *
 * The stored names say "above benchmark". That reads correctly for the fourteen
 * parameters where a high value is the problem, and backwards for the two where
 * it is not: the shared lookup inverts the scale for dissolved oxygen so the
 * COLORS come out right, but the words ride along, and a healthy 8.9 mg/L ends
 * up labeled "below detection limits". pH folds both tails onto one scale and
 * has the same trouble.
 *
 * "Beyond" is the fix: it is true of a high excursion and a low one alike, so
 * one set of names serves all three scales and one legend serves the whole
 * table. The exact numeric range travels in each cell's tooltip, which is what
 * the map's own BenchmarkPopover does and is unambiguous under any scale.
 */
export const BAND_LABELS = {
  "-1": "No benchmark available",
  0: "Well within benchmark",
  1: "Within benchmark",
  2: "Approaching benchmark",
  3: "Beyond benchmark",
  4: "Well beyond benchmark",
};

/** Printed under the legend, because "beyond" needs one sentence of grounding. */
export const BAND_SCALE_NOTE =
  "Green is always the better condition. For most parameters a value beyond " +
  "the benchmark is a high one; for dissolved oxygen it is a low one, and for " +
  "pH either extreme counts. Columns where that differs are marked, and every " +
  "cell's tooltip gives the exact range its shading comes from.";

/** Marks the columns whose scale is not the usual "higher is worse". */
export const SCALE_NOTES = {
  low: "lower is worse",
  ph: "high and low both count",
};

/* ------ Reservoir standards -------------------------------------------- */

/** The three outcomes a regulatory column can show. */
export const ATTAINMENT_LABELS = {
  attaining: "Attaining the standard",
  not_attaining: "Not attaining the standard",
  insufficient: "Too few samples to assess",
};

/** Printed under the standards legend. */
export const STANDARD_SCALE_NOTE =
  "Regulatory columns show the July to September seasonal mean and are " +
  "colored by attainment: at most one season in five may exceed the " +
  "standard. The tooltip names the summers that met and exceeded; the " +
  "calendar icon in the cell opens the full season table.";

/** Appended to the table caption when regulatory columns are on screen. */
export const STANDARD_CAPTION =
  "Columns marked as a standard are July to September seasonal means colored " +
  "by attainment, not by benchmark band.";

export const STANDARD_BADGE = {
  label: "Standard",
  interim: "Interim",
  hint: "Seasonal mean judged against a regulatory standard",
};

/** The band label used for a value that is deliberately shown unshaded. */
export const UNSHADED_LABEL = "Not shaded";

/** Short notes the cell builder drops into tooltips and screen-reader text. */
export const CELL_NOTES = {
  smallSample:
    "Based on fewer than six samples; read this value with caution. The trend " +
    "is tested over its own window and is unaffected.",
  noDataThisPeriod:
    "No samples were collected for this site, parameter and flow condition in this period.",
  neverSampled:
    "Not monitored: this site has never been sampled for this parameter.",
  notOnThisScorecard:
    "This parameter is not part of the scorecard for this site.",
  standardColumnReservoirOnly:
    "Regulatory standard columns apply to reservoir sites only.",
  noJudgedSeasons:
    "No season in this period had enough samples to judge against the standard.",
  unshaded:
    "not applied. These bands were set for streams; a reservoir value is shown " +
    "for reference only.",
  flowNotApplicable:
    "reservoir samples carry no flow condition; every sample is used",
  composite:
    "Composite: the three reservoir stations averaged on each sampling date " +
    "before any statistic was calculated.",
  seasonDetailHint:
    "Select the calendar icon in the cell for every season's mean and sample count.",
};

/** The season-by-season popover behind a reservoir standard cell. */
export const SEASON_DETAIL = {
  buttonLabel: "Season-by-season detail",
  columns: { year: "Summer", mean: "Mean", n: "Samples", verdict: "Verdict" },
  footnote:
    "A season needs at least three samples to be judged. The period attains " +
    "when no more than one season in five exceeds the standard.",
};

/** The markers legend. */
export const MARKER_LEGEND = {
  smallSample:
    "Value from fewer than six samples, shown lighter. The arrow uses its own, longer window.",
  noData:
    "Monitored here, but no samples in this period (tooltip gives the last sample date).",
  dash: "Never sampled here, or the column does not apply to this site.",
  calendar:
    "Opens the season-by-season table behind a reservoir standard cell.",
  map: "Opens the water quality map for that cell (Live period only).",
};

/** Shown at the foot of a cell tooltip when that cell can open the map. */
export const OPEN_IN_MAP_HINT =
  "Select the map icon in this cell to open the site and parameter in the " +
  "Water Quality Map, in a new tab.";

export const CSV_FILENAME_PREFIX = "wq-scorecard";
